import React, {useCallback, useEffect, useState} from 'react';
import {formatDate as formatApiDate} from '../utils/dateTime';
import {useNavigate} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import {useWardrobe} from "../context/WardrobeContext";
import {useToast} from "../context/ToastContext";
import Spinner from "../components/Spinner";
import AuthImage from "../components/AuthImage";
import {ChevronDownIcon, ChevronUpIcon, PlusIcon, SparklesIcon, TrashIcon} from "../components/icons";
import {pluralItems} from '../utils/plural';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

interface OutfitItem {
    id: string;
    name: string;
    brandName: string | null;
    layerName: string | null;
    /** Слот вещи в образе — единая подпись через support/outfitLabels (layerName — фолбэк). */
    slot?: string | null;
    rating: number | null;
    primaryPhotoUrl: string | null;
}

interface SavedOutfit {
    id: string;
    name: string;
    createdAt: string | null;
    items: OutfitItem[];
    aiOverall: number | null;
    wearCount: number;
    lastWornAt: string | null;
}

/** Ответ POST /outfits/saved/{id}/wear (WearOutfitResultDto). */
interface WearOutfitResult {
    wornCount?: number;
    wearId?: string;
    alreadyWornToday?: boolean;
}

type FilterTab = 'all' | 'recent';
type SortKey = 'ai' | 'date' | 'name' | 'wears';
type SortDir = 'desc' | 'asc';

const SORT_OPTIONS: [SortKey, string][] = [
    ['date', 'По дате'],
    ['ai', 'По AI-рейтингу'],
    ['name', 'По названию'],
    ['wears', 'По носкам'],
];

// Светофор по AI-оценке: ≥70 — зелёный, 40–69 — жёлтый, <40 — красный.
function scoreBg(v: number): string {
    if (v >= 70) return 'bg-green-500';
    if (v >= 40) return 'bg-amber-400';
    return 'bg-red-500';
}

const formatDate = (iso: string | null): string =>
    formatApiDate(iso, {day: 'numeric', month: 'long', year: 'numeric'}, '');

// 2×2 photo collage for up to 4 items
const OutfitCollage: React.FC<{items: OutfitItem[]}> = ({items}) => {
    const photos = items.filter(i => i.primaryPhotoUrl).slice(0, 4);
    const empty = 4 - photos.length;

    if (photos.length === 0) {
        return (
            <div className="w-full aspect-square bg-gray-100 dark:bg-ink-800 flex items-center justify-center">
                <SparklesIcon className="w-10 h-10 text-gray-300 dark:text-ink-600"/>
            </div>
        );
    }

    if (photos.length === 1) {
        return (
            <div className="w-full aspect-square overflow-hidden">
                <AuthImage imageKey={photos[0].primaryPhotoUrl!} alt={photos[0].name} variant="thumbnail"
                           className="w-full h-full object-cover"/>
            </div>
        );
    }

    return (
        <div className="w-full aspect-square grid grid-cols-2 grid-rows-2 gap-0.5 bg-gray-200 dark:bg-ink-700">
            {photos.map(item => (
                <div key={item.id} className="overflow-hidden bg-gray-100 dark:bg-ink-800">
                    <AuthImage imageKey={item.primaryPhotoUrl!} alt={item.name} variant="thumbnail"
                               className="w-full h-full object-cover"/>
                </div>
            ))}
            {Array.from({length: empty}).map((_, i) => (
                <div key={`empty-${i}`} className="bg-gray-100 dark:bg-ink-800"/>
            ))}
        </div>
    );
};

const SavedOutfitsPage: React.FC = () => {
    const navigate = useNavigate();
    const {showToast} = useToast();
    const {selectedOwnerId, selectedOwnerDisplayName, isOwnWardrobe, permissions} = useWardrobe();
    const [outfits, setOutfits] = useState<SavedOutfit[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);
    const [tab, setTab] = useState<FilterTab>('all');
    const [sortKey, setSortKey] = useState<SortKey>('date');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [wearingId, setWearingId] = useState<string | null>(null);
    const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);

    const canManage = isOwnWardrobe || (permissions?.includes('MANAGE_LISTS') ?? false);
    const canWear   = isOwnWardrobe || (permissions?.includes('EDIT_ITEMS') ?? false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            const res = await api.get<SavedOutfit[]>('/outfits/saved', {params});
            setOutfits(res.data);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [selectedOwnerId]);

    useDocumentTitle();

    useEffect(() => {
        load();
    }, [load]);

    /** Отмена носки образа (QA-8): снимает запись и носки вещей, созданные вместе с ней. */
    const undoWear = async (id: string, wearId: string) => {
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            await api.delete(`/outfits/saved/${id}/wears/${wearId}`, {params});
            showToast('Носка образа отменена', 'success');
        } catch {
            showToast('Не удалось отменить носку образа', 'error');
        }
    };

    // Одна носка образа в сутки: повторное «Надеть» не плодит запись, но отмену предлагает всегда.
    const wear = async (id: string) => {
        setWearingId(id);
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            const res = await api.post<WearOutfitResult>(`/outfits/saved/${id}/wear`, {}, {params});
            const {wornCount, wearId, alreadyWornToday} = res.data ?? {};
            setMsg(alreadyWornToday
                ? 'Образ уже отмечен как надетый сегодня'
                : `Отмечено как надето (${pluralItems(wornCount ?? 0)})`);
            showToast(
                alreadyWornToday ? 'Уже отмечено сегодня' : 'Образ отмечен как надетый сегодня',
                alreadyWornToday ? 'info' : 'success',
                wearId ? {label: 'Отменить', onClick: () => void undoWear(id, wearId)} : undefined,
            );
        } catch {
            setMsg('Не удалось отметить образ');
            showToast('Не удалось отметить образ', 'error');
        } finally {
            setWearingId(null);
        }
    };

    const remove = async (id: string) => {
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            await api.delete(`/outfits/saved/${id}`, {params});
            setOutfits(prev => prev?.filter(o => o.id !== id) ?? prev);
            setConfirmingDeleteId(null);
        } catch {
            setMsg('Не удалось удалить образ');
            setConfirmingDeleteId(null);
        }
    };

    const list = outfits ?? [];

    const base = tab === 'recent'
        ? [...list].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? '')).slice(0, 10)
        : list;

    const dir = sortDir === 'asc' ? 1 : -1;
    const filteredList = [...base].sort((a, b) => {
        switch (sortKey) {
            case 'ai':
                return dir * ((a.aiOverall ?? -1) - (b.aiOverall ?? -1));
            case 'name':
                return dir * a.name.localeCompare(b.name, 'ru');
            case 'wears':
                return dir * ((a.wearCount ?? 0) - (b.wearCount ?? 0));
            case 'date':
            default:
                return dir * (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
        }
    });

    const title = isOwnWardrobe
        ? 'Образы'
        : `Образы: ${selectedOwnerDisplayName || selectedOwnerId?.slice(0, 8) + '...'}`;

    if (loading) return <Spinner/>;

    if (error) {
        return (
            <div className="p-6 text-center space-y-3">
                <p className="text-gray-600 dark:text-gray-300">Не удалось загрузить образы</p>
                <button onClick={load} className="px-4 py-2 rounded-xl bg-brand-600 text-white hover:bg-brand-700">
                    Повторить
                </button>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-5">
            {/* Header */}
            <div className="flex items-center justify-between gap-3">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h1>
                {canManage && (
                    <button
                        onClick={() => navigate('/outfits/builder')}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-600 text-white hover:bg-brand-700 text-sm font-medium transition-colors"
                    >
                        <PlusIcon className="w-4 h-4"/>
                        Создать образ
                    </button>
                )}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-gray-200 dark:border-ink-700">
                {([['all', 'Все образы'], ['recent', 'Недавние']] as [FilterTab, string][]).map(([key, label]) => (
                    <button
                        key={key}
                        onClick={() => setTab(key)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px
                            ${tab === key
                                ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                            }`}
                    >
                        {label}
                        {key === 'all' && list.length > 0 && (
                            <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500">{list.length}</span>
                        )}
                    </button>
                ))}
            </div>

            {/* Sort controls */}
            {list.length > 0 && (
                <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-400 dark:text-gray-500">Сортировка:</span>
                    <select
                        value={sortKey}
                        onChange={e => setSortKey(e.target.value as SortKey)}
                        aria-label="Сортировка"
                        className="px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-ink-700 bg-white dark:bg-ink-800 text-gray-900 dark:text-gray-100"
                    >
                        {SORT_OPTIONS.map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}
                        title={sortDir === 'asc' ? 'По возрастанию' : 'По убыванию'}
                        aria-label={sortDir === 'asc' ? 'По возрастанию' : 'По убыванию'}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-ink-700 hover:bg-gray-50 dark:hover:bg-ink-800 text-gray-600 dark:text-gray-300"
                    >
                        {sortDir === 'asc'
                            ? <ChevronUpIcon className="w-4 h-4"/>
                            : <ChevronDownIcon className="w-4 h-4"/>}
                        {sortDir === 'asc' ? 'Возр.' : 'Убыв.'}
                    </button>
                </div>
            )}

            {/* Flash message */}
            {msg && (
                <div className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-ink-800 text-sm text-gray-700 dark:text-gray-300">
                    {msg}
                    <button onClick={() => setMsg(null)} className="ml-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">✕</button>
                </div>
            )}

            {/* Empty state */}
            {filteredList.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-ink-800 flex items-center justify-center">
                        <SparklesIcon className="w-8 h-8 text-gray-300 dark:text-ink-600"/>
                    </div>
                    <div>
                        <p className="font-medium text-gray-700 dark:text-gray-300">Образов пока нет</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Соберите свой первый образ через конструктор
                        </p>
                    </div>
                    {canManage && (
                        <button
                            onClick={() => navigate('/outfits/builder')}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 text-white hover:bg-brand-700 text-sm font-medium"
                        >
                            <PlusIcon className="w-4 h-4"/>
                            Создать первый образ
                        </button>
                    )}
                </div>
            )}

            {/* Outfit grid */}
            {filteredList.length > 0 && (
                <div
                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4"
                    data-testid="saved-list"
                >
                    {filteredList.map(outfit => (
                        <div
                            key={outfit.id}
                            onClick={() => navigate(`/outfits/saved/${outfit.id}`)}
                            className="group rounded-xl border border-gray-200 dark:border-ink-700 overflow-hidden bg-white dark:bg-ink-900 transition-shadow hover:shadow-md cursor-pointer"
                        >
                            {/* Collage */}
                            <div className="relative">
                                <OutfitCollage items={outfit.items}/>
                                {/* AI rating badge */}
                                {outfit.aiOverall != null && (
                                    <span
                                        title="AI-оценка совместимости"
                                        className={`absolute top-2 left-2 px-1.5 py-0.5 rounded-md text-xs font-semibold text-white ${scoreBg(outfit.aiOverall)}`}
                                    >
                                        {outfit.aiOverall}%
                                    </span>
                                )}
                                {/* Hover actions overlay */}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-end justify-end p-2 gap-1.5 opacity-0 group-hover:opacity-100">
                                    {canWear && (
                                        <button
                                            onClick={e => { e.stopPropagation(); wear(outfit.id); }}
                                            disabled={wearingId === outfit.id}
                                            title="Надеть образ"
                                            data-testid={`wear-${outfit.id}`}
                                            className="px-2.5 py-1 rounded-lg bg-brand-600 text-white text-xs font-medium hover:bg-brand-700 disabled:opacity-60 transition-colors"
                                        >
                                            {wearingId === outfit.id ? '...' : 'Надеть'}
                                        </button>
                                    )}
                                    {canManage && (
                                        <button
                                            onClick={e => { e.stopPropagation(); setConfirmingDeleteId(outfit.id); }}
                                            title="Удалить образ"
                                            data-testid={`delete-${outfit.id}`}
                                            className="p-1.5 rounded-lg bg-black/60 text-white hover:bg-red-600 transition-colors"
                                        >
                                            <TrashIcon className="w-3.5 h-3.5"/>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Inline delete confirmation */}
                            {confirmingDeleteId === outfit.id && (
                                <div
                                    onClick={e => e.stopPropagation()}
                                    className="px-3 py-2 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800 flex items-center justify-between gap-2 text-xs"
                                >
                                    <span className="text-red-700 dark:text-red-300">Удалить образ?</span>
                                    <div className="flex gap-1.5">
                                        <button
                                            onClick={() => remove(outfit.id)}
                                            data-testid={`delete-confirm-${outfit.id}`}
                                            className="px-2.5 py-1 rounded-lg bg-red-600 text-white hover:bg-red-700 font-medium transition-colors"
                                        >
                                            Да
                                        </button>
                                        <button
                                            onClick={() => setConfirmingDeleteId(null)}
                                            className="px-2.5 py-1 rounded-lg bg-gray-200 dark:bg-ink-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-ink-600 transition-colors"
                                        >
                                            Нет
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Info */}
                            <div className="p-2.5">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {outfit.name}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                    {pluralItems(outfit.items.length)}
                                    {outfit.createdAt && (
                                        <> · {formatDate(outfit.createdAt)}</>
                                    )}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SavedOutfitsPage;
